import {
  PUBLIC_LOCALITY_UNDER_REVIEW_EDITORIAL_LABEL,
  getPublicCatalogLocalityPresentation,
} from '../domain/tourismRules.js';

const ARABIC_TEXT_RE = /[\u0600-\u06ff]/g;
const LATIN_TEXT_RE = /[a-z]/i;
const COMBINING_MARK_RE = /[\u0300-\u036f]/g;
const EDGE_PUNCTUATION_RE = /^[\s,;:|/\\[\]{}'"`._-]+|[\s,;:|/\\[\]{}'"`._-]+$/g;
const PUNCTUATED_LOCALITY_SUFFIX_RE = /\s*(?:[,،]|\s+-\s+)\s*(ehden|zgharta(?:[-\s]+ehden)?|bsharri(?:\s+caza)?|miziara|bkoufa|kfardlakos|ardeh|north lebanon|lebanon)$/i;
const BARE_LOCALITY_SUFFIX_RE = /[\s-]+(ehden|zgharta(?:[-\s]+ehden)?|bsharri(?:\s+caza)?|miziara|bkoufa|kfardlakos|ardeh|north lebanon|lebanon)$/i;

const GENERIC_PUBLIC_NAMES = new Set([
  'lebanon',
  'north lebanon',
  'zgharta',
  'zgharta caza',
  'bsharri',
  'bsharri caza',
  'ehden',
]);

const PROTECTED_PUBLIC_NAMES = new Set([
  'horsh ehden',
]);

function normalizeEnglishText(value) {
  return String(value || '')
    .normalize('NFKD')
    .replace(COMBINING_MARK_RE, '')
    .replace(/[’‘]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\s+/g, ' ')
    .trim();
}

function titleCaseIfAllCaps(value) {
  const letters = value.replace(/[^a-z]/gi, '');
  if (!letters || letters !== letters.toUpperCase()) return value;
  if (!/\s/.test(value.trim())) return value;
  return value.toLowerCase().replace(/\b[a-z]/g, character => character.toUpperCase());
}

function stripLocalitySuffixes(value) {
  let next = value;
  let previous = '';
  while (next && next !== previous) {
    if (PROTECTED_PUBLIC_NAMES.has(next.toLowerCase())) return next;
    previous = next;
    const punctuated = next.replace(PUNCTUATED_LOCALITY_SUFFIX_RE, '');
    const bare = punctuated === next ? next.replace(BARE_LOCALITY_SUFFIX_RE, '') : punctuated;
    next = bare
      .replace(EDGE_PUNCTUATION_RE, '')
      .replace(/\s+/g, ' ')
      .trim();
  }
  return next;
}

function normalizeEnglishPublicName(value) {
  return normalizeEnglishText(value)
    .replace(ARABIC_TEXT_RE, ' ')
    .replace(/[|،]+/g, ' ')
    .replace(/\s+-\s+$/g, ' ')
    .replace(/^\s+-\s+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(EDGE_PUNCTUATION_RE, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function cleanEnglishPublicName(value) {
  const cleaned = stripLocalitySuffixes(normalizeEnglishPublicName(value));

  if (!LATIN_TEXT_RE.test(cleaned)) return '';
  if (GENERIC_PUBLIC_NAMES.has(cleaned.toLowerCase())) return '';
  return titleCaseIfAllCaps(cleaned);
}

export function cleanEnglishPublicLocality(value) {
  const cleaned = normalizeEnglishText(value)
    .replace(/،/g, ',')
    .replace(ARABIC_TEXT_RE, ' ')
    .replace(/\s*,\s*/g, ', ')
    .replace(/^the village,\s*/i, '')
    .replace(EDGE_PUNCTUATION_RE, '')
    .replace(/\s+/g, ' ')
    .trim();

  return LATIN_TEXT_RE.test(cleaned) ? cleaned : '';
}

export function withCleanEnglishPublicName(item) {
  const name = cleanEnglishPublicName(item?.name);
  if (!name) return null;
  if (name === item.name) return item;
  return {
    ...item,
    rawName: item.name,
    name,
  };
}

function appendUniqueEditorialLabel(labels, label) {
  const values = Array.isArray(labels) ? labels.filter(Boolean) : [];
  return values.includes(label) ? values : [...values, label];
}

function toRouteBoundaryCandidate(item) {
  return {
    ...item,
    latitude: item?.coordinates?.lat ?? item?.latitude,
    longitude: item?.coordinates?.lng ?? item?.longitude,
  };
}

export function withPublicEnglishRouteShape(item) {
  const presentation = getPublicCatalogLocalityPresentation(toRouteBoundaryCandidate(item));
  const presented = presentation.needsReview ? {
    ...item,
    rawVillage: item.rawVillage || presentation.rawVillage,
    rawVillageAr: item.rawVillageAr || presentation.rawVillageAr,
    village: presentation.village,
    villageAr: presentation.villageAr,
    catalogLocalityNeedsReview: true,
    catalogLocalityFilterable: presentation.filterable,
    catalogLocalityReviewActions: presentation.actions,
    catalogLocalityReviewReasons: presentation.reasons,
    editorialLabels: appendUniqueEditorialLabel(
      item.editorialLabels,
      PUBLIC_LOCALITY_UNDER_REVIEW_EDITORIAL_LABEL,
    ),
  } : item;

  return withCleanEnglishPublicName(presented);
}

function publicLocality(item) {
  return cleanEnglishPublicLocality(item?.village)
    || cleanEnglishPublicLocality(item?.location);
}

function branchQualifiedName(item) {
  const baseName = item.name;
  const rawEnglishName = normalizeEnglishPublicName(item.rawName || baseName);
  const rawBaseName = cleanEnglishPublicName(rawEnglishName);

  if (
    rawEnglishName
    && rawEnglishName.toLowerCase() !== baseName.toLowerCase()
    && rawBaseName.toLowerCase() === baseName.toLowerCase()
  ) {
    return titleCaseIfAllCaps(rawEnglishName);
  }

  const locality = publicLocality(item);
  return locality ? `${baseName} - ${locality}` : baseName;
}

export function withDistinctPublicEnglishNames(items = []) {
  const localitiesByName = items.reduce((groups, item) => {
    const key = item.name.toLowerCase();
    const locality = publicLocality(item).toLowerCase();
    if (!locality) return groups;
    if (!groups.has(key)) groups.set(key, new Set());
    groups.get(key).add(locality);
    return groups;
  }, new Map());

  return items.map(item => {
    const localities = localitiesByName.get(item.name.toLowerCase());
    if (!localities || localities.size < 2) return item;
    return { ...item, name: branchQualifiedName(item) };
  });
}

export function getPublicEnglishCatalogItems(items = []) {
  return items
    .map(withPublicEnglishRouteShape)
    .filter(Boolean);
}
